import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { CategoryDto, CreateCategoryRequest } from '../../types/admin-api';

interface CategoryFormDialogProps {
  isOpen: boolean;
  category: CategoryDto | null;
  onClose: () => void;
  onSubmit: (data: CreateCategoryRequest) => void;
  isLoading: boolean;
  error: Error | null;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[ąàáâãä]/g, 'a')
    .replace(/[ćčç]/g, 'c')
    .replace(/[ęèéêë]/g, 'e')
    .replace(/[łl]/g, 'l')
    .replace(/[ńñ]/g, 'n')
    .replace(/[óòôõö]/g, 'o')
    .replace(/[śšş]/g, 's')
    .replace(/[źżž]/g, 'z')
    .replace(/[ü]/g, 'u')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function CategoryFormDialog({
  isOpen,
  category,
  onClose,
  onSubmit,
  isLoading,
  error,
}: CategoryFormDialogProps) {
  const isEditing = category !== null;

  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [namePl, setNamePl] = useState('');
  const [descPl, setDescPl] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [descEn, setDescEn] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (category) {
        setSlug(category.slug);
        setIsActive(category.isActive);
        const plTrans = category.translations.find((t) => t.languageCode === 'pl');
        const enTrans = category.translations.find((t) => t.languageCode === 'en');
        setNamePl(plTrans?.name || '');
        setDescPl(plTrans?.description || '');
        setNameEn(enTrans?.name || '');
        setDescEn(enTrans?.description || '');
        setAutoSlug(false);
      } else {
        setSlug('');
        setIsActive(true);
        setNamePl('');
        setDescPl('');
        setNameEn('');
        setDescEn('');
        setAutoSlug(true);
      }
      setErrors({});
    }
  }, [isOpen, category]);

  const handleNamePlChange = (value: string) => {
    setNamePl(value);
    if (autoSlug && !isEditing) {
      setSlug(slugify(value));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!slug.trim()) newErrors.slug = 'Slug is required';
    if (!namePl.trim()) newErrors.namePl = 'Polish name is required';
    if (!nameEn.trim()) newErrors.nameEn = 'English name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const translations = [
      { languageCode: 'pl', name: namePl.trim(), description: descPl.trim() || undefined },
      { languageCode: 'en', name: nameEn.trim(), description: descEn.trim() || undefined },
    ];

    onSubmit({ slug: slug.trim(), isActive, translations });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-app-elevated border border-white/10 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-ink-primary">
            {isEditing ? 'Edit Category' : 'Add Category'}
          </h3>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-3 py-2 rounded text-sm">
              {error instanceof Error ? error.message : 'An error occurred'}
            </div>
          )}

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setAutoSlug(false);
                if (errors.slug) setErrors((prev) => ({ ...prev, slug: '' }));
              }}
              placeholder="e.g. tennis, yoga, swimming"
              className={`w-full px-3 py-2 bg-surface-input border text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.slug ? 'border-brand-warm' : 'border-white/15'
              }`}
            />
            {errors.slug && <p className="mt-1 text-sm text-brand-warm">{errors.slug}</p>}
          </div>

          {/* Active */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-white/15 bg-surface-input text-brand-primary focus:ring-brand-primary"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-white/70">
              Active
            </label>
          </div>

          {/* Polish Translation */}
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-sm font-semibold text-white/80 mb-3">Polish Translation</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Name *</label>
                <input
                  type="text"
                  value={namePl}
                  onChange={(e) => {
                    handleNamePlChange(e.target.value);
                    if (errors.namePl) setErrors((prev) => ({ ...prev, namePl: '' }));
                  }}
                  placeholder="e.g. Tenis"
                  className={`w-full px-3 py-2 bg-surface-input border text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                    errors.namePl ? 'border-brand-warm' : 'border-white/15'
                  }`}
                />
                {errors.namePl && <p className="mt-1 text-sm text-brand-warm">{errors.namePl}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Description</label>
                <textarea
                  value={descPl}
                  onChange={(e) => setDescPl(e.target.value)}
                  placeholder="Optional description in Polish"
                  rows={2}
                  className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* English Translation */}
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-sm font-semibold text-white/80 mb-3">English Translation</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Name *</label>
                <input
                  type="text"
                  value={nameEn}
                  onChange={(e) => {
                    setNameEn(e.target.value);
                    if (errors.nameEn) setErrors((prev) => ({ ...prev, nameEn: '' }));
                  }}
                  placeholder="e.g. Tennis"
                  className={`w-full px-3 py-2 bg-surface-input border text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                    errors.nameEn ? 'border-brand-warm' : 'border-white/15'
                  }`}
                />
                {errors.nameEn && <p className="mt-1 text-sm text-brand-warm">{errors.nameEn}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Description</label>
                <textarea
                  value={descEn}
                  onChange={(e) => setDescEn(e.target.value)}
                  placeholder="Optional description in English"
                  rows={2}
                  className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
